import React, { useMemo, useState } from 'react';
import { useFinalClaims } from '../hook/useFinalClaims.mjs';
import { money, shortDate } from '../util/format.mjs';
import { claimTotals, claimsByPerson } from '../util/aggregate.mjs';
import { periodText as periodTextOf, periodTag, namedRegion } from '../util/period.mjs';
import { exportClaimsByPerson } from '../util/reportsExcel.mjs';
import {
  ScopeToolbar, StatTiles, LoadingCard, ErrorCard, Section, SearchBox, EmptyRow, TH, THR, TD, TDR,
} from './ReportParts.mjs';

const typeLabel = (c) => (c.claimType === 'perdiem' ? 'Per diem' : 'Expense');

// A claim's date is a plain YYYY-MM-DD; parsed as-is it would be UTC midnight
// and show as the day before for anyone west of UTC.
const claimDate = (d) => (d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? shortDate(`${d}T12:00:00`) : shortDate(d));

/**
 * The auditor's view of who was paid: every FINAL claim in the period,
 * grouped by claimant, largest total first. A row opens to that person's
 * claims.
 */
const ClaimsByPersonTab = ({ scope }) => {
  const { data, claims, loading, error, fiscalYears, reload } = useFinalClaims(scope);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(() => new Set());
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  const people = useMemo(() => claimsByPerson(claims), [claims]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return people;
    return people.filter(p => [p.name, p.region, p.position].some(v => String(v || '').toLowerCase().includes(q)));
  }, [people, query]);
  const totals = useMemo(() => claimTotals(claims), [claims]);

  if (!data && loading) return <LoadingCard text="Loading claims..." />;
  if (error && !data) return <ErrorCard error={`Could not load claims: ${error}`} onRetry={reload} />;

  const periodText = periodTextOf(scope.fy, scope.quarter);
  const toggle = (id) => setOpen(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      // The search narrows the export too, so the file matches the screen.
      await exportClaimsByPerson({
        claims: shown.flatMap(p => p.list),
        fyLabel: periodText,
        fyShort: periodTag(scope.fy, scope.quarter),
        region: namedRegion(scope, data?.region),
      });
    } catch (err) {
      setExportError(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <ScopeToolbar
        scope={scope}
        fiscalYears={fiscalYears}
        onExport={handleExport}
        exporting={exporting}
        exportDisabled={shown.length === 0}
        loading={loading}
        note="Final claims only: staff claims in a finally approved monthly report, and contractor claims you have given final approval. Nothing in draft or still in review is listed."
      />
      {(error || exportError) && (
        <div className="mb-4"><ErrorCard error={exportError || `Could not refresh: ${error}`} /></div>
      )}

      <StatTiles tiles={[
        { label: 'Claimants', value: String(totals.claimants), hint: periodText },
        { label: 'Claims', value: String(totals.claims), hint: `${totals.perDiemClaims} per diem · ${totals.expenseClaims} expense` },
        { label: 'Total paid', value: money(totals.total), hint: `Per diem ${money(totals.perDiem)} · Expense ${money(totals.expense)}` },
      ]} />

      <Section title="Claims by person" subtitle="Largest total first. Open a row for that person's claims.">
        <div className="px-4 sm:px-6 py-3 border-b border-gray-100">
          <SearchBox value={query} onChange={setQuery} label="Search claimants" placeholder="Name, region or position" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stanford-red text-white">
              <tr>
                <th className={TH}>Name</th>
                <th className={TH}>Region</th>
                <th className={TH}>Position</th>
                <th className={THR}>Claims</th>
                <th className={THR}>Total</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(p => {
                const isOpen = open.has(p.id);
                return (
                  <React.Fragment key={p.id}>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className={`${TD} font-medium text-gray-800`}>
                        <button
                          onClick={() => toggle(p.id)}
                          aria-expanded={isOpen}
                          className="flex items-center gap-2 text-left hover:text-stanford-red focus:outline-none focus-visible:ring-2 focus-visible:ring-stanford-red rounded"
                        >
                          <span aria-hidden="true" className="inline-block w-3 text-gray-500">{isOpen ? '\u25BE' : '\u25B8'}</span>
                          {p.name}
                        </button>
                      </td>
                      <td className={TD}>{p.region || '-'}</td>
                      <td className={TD}>{p.position || <span className="text-gray-400">Not recorded</span>}</td>
                      <td className={TDR}>
                        {p.claims}
                        <div className="text-xs text-gray-500">{p.perDiemClaims} per diem / {p.expenseClaims} expense</div>
                      </td>
                      <td className={`${TDR} font-medium text-gray-800`}>{money(p.total)}</td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <td colSpan={5} className="px-4 sm:px-6 py-3">
                          <table className="w-full">
                            <thead>
                              <tr className="text-gray-600">
                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide">Date</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide">Type</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide">Amount</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide">Report</th>
                              </tr>
                            </thead>
                            <tbody>
                              {p.list.map(c => (
                                <tr key={`${c.claimType}-${c.claimId}`} className="border-t border-gray-200">
                                  <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">{claimDate(c.date)}</td>
                                  <td className="px-3 py-2 text-sm text-gray-700">{typeLabel(c)}</td>
                                  <td className="px-3 py-2 text-sm text-gray-800 text-right tabular-nums">{money(c.amountUsd)}</td>
                                  <td className="px-3 py-2 text-sm text-gray-700">
                                    {c.reportLabel || (c.reportId
                                      ? String(c.reportId).slice(-8).toUpperCase()
                                      : <span className="text-gray-500">Approved by the auditor, not in a monthly report</span>)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {shown.length === 0 && (
                <EmptyRow
                  colSpan={5}
                  text={people.length === 0 ? `No final claims in ${periodText}.` : 'No claimant matches that search.'}
                />
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </>
  );
};

export default ClaimsByPersonTab;
